import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

const EmailTemplateManagement = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [alert, setAlert] = useState(null);

  // Load templates
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/email/templates/all');
      
      if (data.success) {
        setTemplates(data.data);
      } else {
        setAlert({ severity: 'error', message: 'Failed to load templates' });
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setAlert({ severity: 'error', message: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    navigate('/superadmin/email-templates/new');
  };

  const handleEditTemplate = (template) => {
    navigate(`/superadmin/email-templates/edit/${template.id}`);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const data = await apiService.delete(`/email/templates/${templateId}`);
      
      if (data.success) {
        setAlert({ severity: 'success', message: 'Template deleted successfully' });
        loadTemplates();
      } else {
        setAlert({ severity: 'error', message: data.message || 'Failed to delete template' });
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      setAlert({ severity: 'error', message: 'Failed to delete template' });
    }
  };

  const handleViewTemplate = (template) => {
    setViewingTemplate(template);
  };

  const handleCopyTemplate = (template) => {
    // Navigate to create form with pre-filled data
    const queryParams = new URLSearchParams({
      copy: 'true',
      name: `${template.name} (Copy)`,
      subject: template.subject,
      description: template.description,
      body_html: template.body_html || template.body || '',
      body_text: template.body_text || '',
      variables: template.variables || '[]',
      is_active: 'true'
    });
    navigate(`/superadmin/email-templates/new?${queryParams.toString()}`);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Email Template Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Create New Template
        </Button>
      </Box>

      {alert && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {/* Templates Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Templates ({templates.length})
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Variables</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Loading templates...</TableCell>
                  </TableRow>
                ) : templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No templates found</TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{template.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {template.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {template.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {template.variables ? 
                            JSON.parse(template.variables).map((variable, index) => (
                              <Chip 
                                key={index} 
                                label={variable} 
                                size="small" 
                                variant="outlined"
                              />
                            ))
                            : 
                            <Typography variant="body2" color="text.secondary">No variables</Typography>
                          }
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={template.is_active ? 'Active' : 'Inactive'} 
                          color={template.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewTemplate(template)}
                            title="View Template"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditTemplate(template)}
                            title="Edit Template"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyTemplate(template)}
                            title="Copy Template"
                          >
                            <CopyIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTemplate(template.id)}
                            title="Delete Template"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>



      {/* View Template Dialog */}
      <Dialog 
        open={!!viewingTemplate} 
        onClose={() => setViewingTemplate(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Template: {viewingTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {viewingTemplate && (
            <Box>
              <Typography variant="h6" gutterBottom>Subject</Typography>
              <Typography variant="body1" sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                {viewingTemplate.subject}
              </Typography>
              
              <Typography variant="h6" gutterBottom>Description</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {viewingTemplate.description}
              </Typography>
              
              <Typography variant="h6" gutterBottom>HTML Body</Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1, 
                  maxHeight: 400, 
                  overflow: 'auto',
                  border: '1px solid #ddd'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: viewingTemplate.body_html || viewingTemplate.body || 'No HTML content' 
                }}
              />
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Variables</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {viewingTemplate.variables ? 
                  JSON.parse(viewingTemplate.variables).map((variable, index) => (
                    <Chip 
                      key={index} 
                      label={variable} 
                      size="small" 
                      color="primary"
                    />
                  ))
                  : 
                  <Typography variant="body2" color="text.secondary">No variables</Typography>
                }
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingTemplate(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateManagement;
